import { useState, useEffect } from 'react';
import AdminLayout from '@/pages/admin/components/AdminLayout';

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone: string;
  inquiry_type: string;
  company: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

const EDGE_FUNCTION_URL = 'https://fdxjebyoqhgkrbiofazf.supabase.co/functions/v1/admin-messages';

function getToken(): string {
  return localStorage.getItem('koorpet_admin_token') || '';
}

async function callEdge(action: string, payload: Record<string, unknown> = {}) {
  const res = await fetch(EDGE_FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, token: getToken(), ...payload }),
  });
  return res.json();
}

export default function AdminMessages() {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<ContactMessage | null>(null);
  const [notificationEmail, setNotificationEmail] = useState('cs@koorpet.com');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [emailSaveMsg, setEmailSaveMsg] = useState('');
  const [webhookSaveMsg, setWebhookSaveMsg] = useState('');
  const [testWebhookLoading, setTestWebhookLoading] = useState(false);
  const [testWebhookMsg, setTestWebhookMsg] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ContactMessage | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchMessages();
    fetchSettings();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await callEdge('list');
      if (result.success) {
        setMessages(result.data || []);
      } else {
        setError('加载留言失败：' + (result.error || '未知错误'));
      }
    } catch {
      setError('请求失败，请检查网络连接');
    }
    setLoading(false);
  };

  const fetchSettings = async () => {
    try {
      const result = await callEdge('get_settings');
      if (result.success) {
        if (result.notification_email) setNotificationEmail(result.notification_email);
        if (result.webhook_url) setWebhookUrl(result.webhook_url);
      }
    } catch {
      // settings fetch is non-critical
    }
  };

  const markAsRead = async (id: string) => {
    try {
      const result = await callEdge('mark_read', { messageId: id });
      if (result.success) {
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, is_read: true } : m)));
        if (selected?.id === id) setSelected((s) => s ? { ...s, is_read: true } : null);
      }
    } catch {
      // ignore
    }
  };

  const confirmDelete = (msg: ContactMessage) => {
    setDeleteTarget(msg);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await callEdge('delete', { messageId: deleteTarget.id });
      if (result.success) {
        setMessages((prev) => prev.filter((m) => m.id !== deleteTarget.id));
        if (selected?.id === deleteTarget.id) setSelected(null);
        setDeleteTarget(null);
      }
    } catch {
      // ignore
    }
    setDeleting(false);
  };

  const saveEmail = async () => {
    setEmailSaving(true);
    setEmailSaveMsg('');
    try {
      const result = await callEdge('save_settings', { notification_email: notificationEmail });
      if (result.success) {
        setEmailSaveMsg('邮箱保存成功！');
        setTimeout(() => setEmailSaveMsg(''), 3000);
      } else {
        setEmailSaveMsg('保存失败：' + (result.error || '未知错误'));
      }
    } catch {
      setEmailSaveMsg('保存失败：网络错误');
    }
    setEmailSaving(false);
  };

  const saveWebhook = async () => {
    setWebhookSaving(true);
    setWebhookSaveMsg('');
    try {
      const result = await callEdge('save_settings', { webhook_url: webhookUrl });
      if (result.success) {
        setWebhookSaveMsg('Webhook URL 保存成功！');
        setTimeout(() => setWebhookSaveMsg(''), 3000);
      } else {
        setWebhookSaveMsg('保存失败：' + (result.error || '未知错误'));
      }
    } catch {
      setWebhookSaveMsg('保存失败：网络错误');
    }
    setWebhookSaving(false);
  };

  const testWebhook = async () => {
    setTestWebhookLoading(true);
    setTestWebhookMsg('');
    try {
      const result = await callEdge('test_webhook');
      if (result.success) {
        setTestWebhookMsg('推送成功！请检查企业微信群消息。');
        setTimeout(() => setTestWebhookMsg(''), 5000);
      } else {
        setTestWebhookMsg('推送失败：' + (result.error || result.message || '未知错误'));
      }
    } catch {
      setTestWebhookMsg('推送失败：网络错误');
    }
    setTestWebhookLoading(false);
  };

  const formatDate = (d: string) => {
    return new Date(d).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <AdminLayout title="留言管理">
      {/* Email + Webhook Config Bar */}
      <div className="mb-6 bg-white rounded-xl border border-[#ede9e3] p-4 flex flex-col gap-4">
        {/* Email Notification */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-[#555] flex-shrink-0">
            <i className="ri-mail-settings-line text-[#C8714A]"></i>
            <span>新留言邮件通知：</span>
          </div>
          <input
            type="email"
            value={notificationEmail}
            onChange={(e) => setNotificationEmail(e.target.value)}
            placeholder="cs@koorpet.com"
            className="flex-1 px-3 py-2 rounded-lg border border-[#e8e4de] text-sm focus:outline-none focus:border-[#C8714A]"
          />
          <button
            onClick={saveEmail}
            disabled={emailSaving}
            className="px-4 py-2 rounded-lg bg-[#C8714A] text-white text-sm font-medium hover:bg-[#b5603b] disabled:opacity-60 cursor-pointer whitespace-nowrap"
          >
            {emailSaving ? '保存中...' : '保存'}
          </button>
          {emailSaveMsg && (
            <span className={`text-xs ${emailSaveMsg.includes('失败') ? 'text-red-500' : 'text-green-600'}`}>{emailSaveMsg}</span>
          )}
        </div>

        {/* Webhook - 企业微信 */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-3 border-t border-[#f0ebe4]">
          <div className="flex items-center gap-2 text-sm text-[#555] flex-shrink-0">
            <i className="ri-link text-[#C8714A]"></i>
            <span>企业微信 Webhook：</span>
          </div>
          <input
            type="url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=..."
            className="flex-1 px-3 py-2 rounded-lg border border-[#e8e4de] text-sm focus:outline-none focus:border-[#C8714A]"
          />
          <button
            onClick={saveWebhook}
            disabled={webhookSaving}
            className="px-4 py-2 rounded-lg bg-[#C8714A] text-white text-sm font-medium hover:bg-[#b5603b] disabled:opacity-60 cursor-pointer whitespace-nowrap"
          >
            {webhookSaving ? '保存中...' : '保存'}
          </button>
          <button
            onClick={testWebhook}
            disabled={testWebhookLoading}
            className="px-4 py-2 rounded-lg border border-[#C8714A] text-[#C8714A] text-sm font-medium hover:bg-[#fdf8f5] disabled:opacity-60 cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            {testWebhookLoading ? (
              <><i className="ri-loader-4-line animate-spin"></i>测试中...</>
            ) : (
              <><i className="ri-send-plane-line"></i>测试推送</>
            )}
          </button>
          {webhookSaveMsg && (
            <span className={`text-xs ${webhookSaveMsg.includes('失败') ? 'text-red-500' : 'text-green-600'}`}>{webhookSaveMsg}</span>
          )}
          {testWebhookMsg && (
            <span className={`text-xs ${testWebhookMsg.includes('失败') ? 'text-red-500' : 'text-green-600'}`}>{testWebhookMsg}</span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-error-warning-line"></i></div>
          {error}
          <button onClick={fetchMessages} className="ml-auto text-[#C8714A] cursor-pointer whitespace-nowrap">重试</button>
        </div>
      )}

      {/* Messages Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* List */}
        <div className="lg:col-span-1 flex flex-col gap-2 max-h-[calc(100vh-360px)] overflow-y-auto">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-[#999]">共 {messages.length} 条</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-[#aaa] text-sm">
              <i className="ri-loader-4-line animate-spin mr-2"></i>加载中...
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-10 text-[#aaa] text-sm">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-[#f7f5f2] mb-3">
                <i className="ri-message-2-line text-xl"></i>
              </div>
              暂无留言
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`group p-4 rounded-xl border transition-all ${
                  selected?.id === msg.id
                    ? 'border-[#C8714A] bg-[#fdf8f5]'
                    : 'border-[#ede9e3] bg-white hover:border-[#C8714A]/40'
                } ${!msg.is_read ? 'border-l-2 border-l-[#C8714A]' : ''}`}
              >
                <div
                  onClick={() => {
                    setSelected(msg);
                    if (!msg.is_read) markAsRead(msg.id);
                  }}
                  className="cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-semibold text-[#1a1a1a] truncate max-w-[120px]">{msg.name}</span>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${msg.is_read ? 'bg-transparent' : 'bg-[#C8714A]'}`}></span>
                  </div>
                  <div className="text-xs text-[#888] truncate mb-1">{msg.inquiry_type || '未分类'}</div>
                  <div className="text-xs text-[#aaa] line-clamp-2 mb-2">{msg.message}</div>
                  <div className="text-xs text-[#bbb]">{formatDate(msg.created_at)}</div>
                </div>
                <div className="flex items-center gap-1 mt-2 pt-2 border-t border-[#f0ebe4] opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(msg);
                      if (!msg.is_read) markAsRead(msg.id);
                    }}
                    className="px-2 py-1 rounded text-xs text-[#888] hover:text-[#C8714A] hover:bg-[#fdf8f5] cursor-pointer whitespace-nowrap"
                  >
                    查看
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmDelete(msg);
                    }}
                    className="px-2 py-1 rounded text-xs text-red-400 hover:text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap ml-auto"
                  >
                    <i className="ri-delete-bin-line mr-0.5"></i>删除
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="bg-white rounded-xl border border-[#ede9e3] p-10 text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-[#f7f5f2] mb-4">
                <i className="ri-mail-open-line text-2xl text-[#ccc]"></i>
              </div>
              <p className="text-[#aaa] text-sm">选择左侧留言查看详情</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#ede9e3] p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-[#1a1a1a]">留言详情</h3>
                <div className="flex items-center gap-2">
                  {!selected.is_read && (
                    <button
                      onClick={() => markAsRead(selected.id)}
                      className="px-3 py-1.5 rounded-lg border border-[#C8714A] text-xs text-[#C8714A] cursor-pointer whitespace-nowrap hover:bg-[#fdf8f5]"
                    >
                      标记已读
                    </button>
                  )}
                  <button
                    onClick={() => confirmDelete(selected)}
                    className="px-3 py-1.5 rounded-lg border border-red-200 text-xs text-red-400 cursor-pointer whitespace-nowrap hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>删除
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InfoRow icon="ri-user-line" label="姓名" value={selected.name} />
                  <InfoRow icon="ri-mail-line" label="邮箱" value={selected.email} />
                  <InfoRow icon="ri-phone-line" label="电话" value={selected.phone || '-'} />
                  <InfoRow icon="ri-price-tag-3-line" label="类型" value={selected.inquiry_type || '-'} />
                  {selected.company && <InfoRow icon="ri-building-line" label="公司" value={selected.company} />}
                </div>
                <div className="pt-4 border-t border-[#f0ebe4]">
                  <div className="text-xs font-semibold text-[#555] mb-2 flex items-center gap-1.5">
                    <i className="ri-chat-1-line"></i>留言内容
                  </div>
                  <p className="text-sm text-[#444] leading-relaxed whitespace-pre-wrap bg-[#faf8f5] rounded-xl p-4">{selected.message}</p>
                </div>
                <div className="text-xs text-[#bbb]">
                  提交时间：{formatDate(selected.created_at)}
                </div>
                <div className="flex gap-2 pt-2">
                  <a
                    href={`mailto:${selected.email}?subject=Re: ${selected.inquiry_type || '留言回复'}`}
                    className="px-4 py-2 rounded-full bg-[#C8714A] text-white text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-[#b5603b] flex items-center gap-1.5"
                  >
                    <i className="ri-reply-line"></i>回复邮件
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-lg">
            <h3 className="text-base font-semibold text-[#1a1a1a] mb-2">确认删除</h3>
            <p className="text-sm text-[#666] mb-1">
              确定要删除 <span className="font-medium text-[#1a1a1a]">{deleteTarget.name}</span> 的留言吗？
            </p>
            <p className="text-xs text-[#aaa] mb-5">此操作不可恢复</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#555] cursor-pointer whitespace-nowrap hover:bg-[#f7f5f2]"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-red-600 disabled:opacity-60 flex items-center gap-1.5"
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>删除中...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>确认删除
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#f7f5f2] flex-shrink-0">
        <i className={`${icon} text-xs text-[#888]`}></i>
      </div>
      <div>
        <div className="text-xs text-[#999]">{label}</div>
        <div className="text-sm text-[#333]">{value}</div>
      </div>
    </div>
  );
}